import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  LinearProgress,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  PlayArrow,
  CheckCircle,
  RadioButtonUnchecked,
  ArrowBack,
  NavigateNext,
  NavigateBefore,
  Description,
  VideoLibrary,
  PictureAsPdf,
  YouTube,
  Quiz,
  Info,
  Lock,
  Cancel,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'

import { RootState, AppDispatch } from '../app/store'
import { fetchCourseById, enrollInCourse } from '../features/courses/courseSlice'
import { fetchUserEnrollments } from '../features/assignments/assignmentSlice'
import { fetchAssessmentById } from '../features/assessments/assessmentSlice'
import { Chapter, UserEnrollment } from '../types'
import * as courseAPI from '../api/courses'
import AssessmentTaking from '../components/AssessmentTaking'
import ShowResultsDialog from '../components/ShowResultsDialog'

const CourseViewPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  const { currentCourse, isLoading, error } = useSelector((state: RootState) => state.courses)
  const { userEnrollments, enrollmentsLoaded } = useSelector((state: RootState) => state.assignments)
  const { currentAssessment } = useSelector((state: RootState) => state.assessments)

  const [currentChapterIndex, setCurrentChapterIndex] = useState(0)
  const [currentItemId, setCurrentItemId] = useState<string | null>(null)
  const [currentItemType, setCurrentItemType] = useState<'chapter' | 'assessment'>('chapter')
  const [completedChapters, setCompletedChapters] = useState<Set<number>>(new Set())
  const [isTakingAssessment, setIsTakingAssessment] = useState(false)
  const [showingAssessmentResults, setShowingAssessmentResults] = useState(false)
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [errorDialogMessage, setErrorDialogMessage] = useState('')
  const [showResultsDialogOpen, setShowResultsDialogOpen] = useState(false)
  const [selectedResultsAssessment, setSelectedResultsAssessment] = useState<{ id: number; title: string } | null>(null)
  
  // Ref to preserve sidebar scroll position during data refreshes
  const sidebarRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (courseId) {
      dispatch(fetchCourseById(courseId))
      dispatch(fetchUserEnrollments())
    }
  }, [dispatch, courseId])

  const enrollment = userEnrollments.find((e: UserEnrollment) => {
    if (!e.assignment || e.assignment.content_type !== 'course') return false
    
    const courseIdToMatch = parseInt(courseId || '0', 10)
    const enrollmentCourseId = typeof e.assignment.course === 'object'
      ? e.assignment.course?.id
      : parseInt(String(e.assignment.course), 10)
    
    return enrollmentCourseId === courseIdToMatch
  })
  
  const course = currentCourse
  const chapters = course?.chapters || []

  // Create unified item sequence from sections
  const itemSequence = useMemo(() => {
    if (!course?.sections || course.sections.length === 0) {
      // Fallback to chapters only
      return chapters.map((chapter, index) => ({
        id: `chapter-${chapter.id}`,
        type: 'chapter' as const,
        data: chapter,
        chapterIndex: index,
        title: chapter.title,
        is_locked: false, // chapters don't have locks in old structure
        courseItemId: undefined // no CourseItem for old structure
      }))
    }
    
    const sequence: Array<{
      id: string
      type: 'chapter' | 'assessment'
      data: any
      chapterIndex?: number
      title: string
      is_locked: boolean
      courseItemId?: number  // Add CourseItem ID for progress tracking
    }> = []
    
    course.sections.forEach(section => {
      section.items?.forEach(item => {
        if (item.item_type === 'chapter' && item.chapter_detail) {
          const chapterIndex = chapters.findIndex(ch => ch.id === item.chapter_detail?.id)
          if (chapterIndex !== -1) {
            sequence.push({
              id: `chapter-${item.chapter_detail.id}`,
              type: 'chapter',
              data: item.chapter_detail,
              chapterIndex,
              title: item.chapter_detail.title,
              is_locked: item.computed_is_locked !== undefined ? item.computed_is_locked : (item.is_unlocked !== undefined ? !item.is_unlocked : item.is_locked ?? false),
              courseItemId: item.id  // Store CourseItem ID
            })
          }
        } else if (item.item_type === 'assessment' && item.assessment_detail) {
          sequence.push({
            id: `assessment-${item.assessment_detail.id}`,
            type: 'assessment',
            data: item.assessment_detail,
            title: item.assessment_detail.title,
            is_locked: item.computed_is_locked !== undefined ? item.computed_is_locked : (item.is_unlocked !== undefined ? !item.is_unlocked : item.is_locked ?? false),
            courseItemId: item.id  // Store CourseItem ID
          })
        }
      })
    })
    
    return sequence
  }, [course?.sections, chapters])

  // Initialize current item when course loads
  useEffect(() => {
    if (itemSequence.length > 0 && !currentItemId) {
      const firstItem = itemSequence[0]
      setCurrentItemId(firstItem.id)
      setCurrentItemType(firstItem.type)
      if (firstItem.type === 'chapter' && firstItem.chapterIndex !== undefined) {
        setCurrentChapterIndex(firstItem.chapterIndex)
      }
    }
  }, [itemSequence.length, currentItemId])

  useEffect(() => {
    // Determine completed chapters from course section items' user_progress
    // This is more accurate than using progress percentage
    if (course?.sections && course.sections.length > 0) {
      const completedSet = new Set<number>()
      
      console.log('Calculating completed chapters from sections...')
      
      course.sections.forEach(section => {
        section.items?.forEach(item => {
          if (item.item_type === 'chapter' && item.chapter_detail && item.user_progress?.completed) {
            // Find the chapter index in the chapters array
            const chapterIndex = chapters.findIndex(ch => ch.id === item.chapter_detail?.id)
            if (chapterIndex !== -1) {
              completedSet.add(chapterIndex)
              console.log(`Chapter ${chapterIndex} (${item.chapter_detail.title}) marked as completed`)
            }
          }
        })
      })
      
      console.log('Completed chapters:', Array.from(completedSet))
      setCompletedChapters(completedSet)
    } else if (enrollment && chapters.length > 0) {
      // Fallback: Calculate completed chapters based on progress percentage
      // This is less accurate but works for courses without section structure
      const progressPercentage = Number(enrollment.progress) || 0
      const chaptersCompleted = Math.round((progressPercentage * chapters.length) / 100)
      
      const completedSet = new Set<number>()
      for (let i = 0; i < chaptersCompleted; i++) {
        completedSet.add(i)
      }
      setCompletedChapters(completedSet)
    }
  }, [course?.sections, chapters, enrollment?.progress])

  const handleEnroll = async () => {
    if (!courseId) return
    
    try {
      await dispatch(enrollInCourse(courseId)).unwrap()
      // Success - enrollment created, refresh enrollments to update UI
      await dispatch(fetchUserEnrollments()).unwrap()
    } catch (error: any) {
      console.error('❌ Enrollment failed:', error)
      
      // If already enrolled, refresh the enrollments data to sync the UI
      if (error?.includes?.('Already enrolled') || error?.response?.data?.error?.includes?.('Already enrolled')) {
        await dispatch(fetchUserEnrollments()).unwrap()
      }
    }
  }

  const handleItemClick = (itemId: string, itemType: 'chapter' | 'assessment') => {
    const item = itemSequence.find(i => i.id === itemId)
    if (!item) return
    
    // Check if item is locked
    if (item.is_locked) {
      // Could show a tooltip or alert here
      return
    }
    
    setCurrentItemId(itemId)
    setCurrentItemType(itemType)
    setIsTakingAssessment(false) // Reset assessment taking state
    setShowingAssessmentResults(false) // Reset results state
    
    if (itemType === 'chapter' && item.chapterIndex !== undefined) {
      setCurrentChapterIndex(item.chapterIndex)
    } else if (itemType === 'assessment') {
      // Fetch assessment details including submission status
      const assessmentId = item.data.id.toString()
      dispatch(fetchAssessmentById(assessmentId))
    }
  }

  const handleChapterClick = (index: number) => {
    const chapterItem = itemSequence.find(item => 
      item.type === 'chapter' && item.chapterIndex === index
    )
    if (chapterItem) {
      handleItemClick(chapterItem.id, 'chapter')
    } else {
      // Fallback for old structure
      setCurrentChapterIndex(index)
      setCurrentItemId(`chapter-${chapters[index]?.id}`)
      setCurrentItemType('chapter')
    }
  }

  const handleChapterComplete = async () => {
    if (!enrollment) {
      console.error('No enrollment found for this course')
      return
    }

    const currentItem = itemSequence.find(item => item.id === currentItemId)
    
    console.log('handleChapterComplete called:', {
      currentItemId,
      currentItem: currentItem ? {
        id: currentItem.id,
        title: currentItem.title,
        courseItemId: currentItem.courseItemId,
        type: currentItem.type
      } : null,
      enrollmentId: enrollment.id
    })
    
    // Check if courseItemId exists (required for new course structure)
    if (!currentItem?.courseItemId) {
      console.error('Missing courseItemId - cannot mark as complete')
      setErrorDialogMessage('This course uses an older structure that does not support progress tracking. Please contact your administrator.')
      setShowErrorDialog(true)
      return
    }
    
    try {
      // Preserve sidebar scroll position before refresh
      const scrollPosition = sidebarRef.current?.scrollTop || 0
      
      console.log('Calling API to mark item complete:', currentItem.courseItemId)
      
      // Mark the CourseItemProgress as completed (triggers unlock signal in backend)
      const result = await courseAPI.completeCourseItemByItem(currentItem.courseItemId, enrollment.id)
      console.log('API call succeeded:', result)
      
      // Update local completed chapters state for UI feedback
      const newCompleted = new Set(completedChapters)
      newCompleted.add(currentChapterIndex)
      setCompletedChapters(newCompleted)
      console.log('Updated local completed chapters:', Array.from(newCompleted))
      
      // Refresh enrollments to get updated progress and unlock status
      console.log('Fetching updated enrollments...')
      await dispatch(fetchUserEnrollments()).unwrap()
      
      // Refetch course data to get updated lock status
      if (courseId) {
        console.log('Fetching updated course data...')
        const updatedCourse = await dispatch(fetchCourseById(courseId)).unwrap()
        console.log('Course refetched:', {
          sectionsCount: updatedCourse?.sections?.length,
          hasUserProgress: !!updatedCourse?.sections?.[0]?.items?.[0]?.user_progress
        })
      }
      
      // Restore sidebar scroll position after refresh
      requestAnimationFrame(() => {
        if (sidebarRef.current) {
          sidebarRef.current.scrollTop = scrollPosition
        }
      })
      
    } catch (error: any) {
      console.error('Failed to mark chapter complete:', {
        response: error.response?.data,
        status: error.response?.status
      })
      setErrorDialogMessage(`Failed to save progress: ${error.response?.data?.error || error.message || 'Unknown error'}`)
      setShowErrorDialog(true)
    }
  }

  // Helper function to get prerequisite names for locked items
  const getPrerequisiteNames = (item: any) => {
    if (!item.prerequisites_detail || item.prerequisites_detail.length === 0) {
      return null
    }
    return item.prerequisites_detail.map((prereq: any) => prereq.title).join(', ')
  }

  // Assessment completion is now handled automatically by the backend when 
  // the assessment is graded and passed (via handle_assessment_submission signal).
  // No manual handleAssessmentComplete function needed.

  const handleNextItem = () => {
    const currentIndex = itemSequence.findIndex(item => item.id === currentItemId)
    if (currentIndex < itemSequence.length - 1) {
      const nextItem = itemSequence[currentIndex + 1]
      handleItemClick(nextItem.id, nextItem.type)
    }
  }

  const handlePrevItem = () => {
    const currentIndex = itemSequence.findIndex(item => item.id === currentItemId)
    if (currentIndex > 0) {
      const prevItem = itemSequence[currentIndex - 1]
      handleItemClick(prevItem.id, prevItem.type)
    }
  }

  const getContentIcon = (contentType: string) => {
    switch (contentType) {
      case 'video': return <VideoLibrary />
      case 'pdf': return <PictureAsPdf />
      case 'youtube': return <YouTube />
      default: return <Description />
    }
  }

  const getYouTubeEmbedUrl = (url: string) => {
    if (!url) return ''

    // If it's already an embed URL, return as is
    if (url.includes('youtube.com/embed/')) {
      return url
    }

    // Extract video ID from various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /^[a-zA-Z0-9_-]{11}$/ // Just the video ID
    ]

    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match) {
        return `https://www.youtube.com/embed/${match[1]}`
      }
    }

    return url // Fallback
  }

  // Helper function to check if an assessment is completed
  const isAssessmentCompleted = (assessmentId: number) => {
    // Check if this assessment has been completed AND passed with minimum grade
    // Similar to the logic in renderAssessmentContent
    const assessmentEnrollment = userEnrollments.find(
      (e: any) => 
        e.assignment?.assessment?.id === assessmentId
    )
    
    // Get assessment details to check scores
    const assessmentDetail = itemSequence.find(item => 
      item.type === 'assessment' && item.data.id === assessmentId
    )?.data
    
    if (!assessmentDetail) return false
    
    // Check if status is completed (means passed)
    if (assessmentEnrollment?.status === 'completed') return true
    
    // Check if user has passed based on best score vs minimum pass percentage
    const bestScore = assessmentDetail?.user_submission_status?.best_score ?? 0
    const minPassPercentage = assessmentDetail?.min_pass_percentage ?? 60
    const hasPassed = bestScore >= minPassPercentage
    
    // Only mark as completed if user has a submission AND has passed
    const hasSubmission = assessmentDetail?.user_submission_status?.has_submission || 
                          (bestScore !== undefined && bestScore !== null && bestScore > 0)
    
    return hasSubmission && hasPassed
  }

  const isAssessmentFailed = (assessmentId: number) => {
    // Check if assessment has been attempted but failed to meet minimum grade
    const assessmentDetail = itemSequence.find(item => 
      item.type === 'assessment' && item.data.id === assessmentId
    )?.data
    
    if (!assessmentDetail) return false
    
    const bestScore = assessmentDetail?.user_submission_status?.best_score ?? 0
    const minPassPercentage = assessmentDetail?.min_pass_percentage ?? 60
    const hasSubmission = assessmentDetail?.user_submission_status?.has_submission || 
                          (bestScore !== undefined && bestScore !== null && bestScore > 0)
    
    // Failed if has submission but didn't pass
    return hasSubmission && (bestScore < minPassPercentage)
  }

  const renderChapterContent = (chapter: Chapter) => {

    switch (chapter.content_type) {
      case 'video':
      case 'youtube':
        const videoUrl = chapter.youtube_url || chapter.video_url
        return videoUrl ? (
          // Center the video container and keep it responsive
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', px: 2 }}>
            <Box sx={{ width: '100%', maxWidth: '900px' }}>
              <Box sx={{
                position: 'relative',
                width: '100%',
                paddingBottom: '56.25%', // 16:9 aspect ratio
                height: 0,
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}>
                <iframe
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '8px',
                  }}
                  src={getYouTubeEmbedUrl(videoUrl)}
                  title={`Video: ${chapter.title}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <Typography>No video URL provided</Typography>
        )

      default:
        return (
          // Constrain and center long text content so the layout doesn't break
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: '100%', maxWidth: '900px', px: 1 }}>
              <Box
                className="tinymce-content"
                sx={{
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
                dangerouslySetInnerHTML={{ __html: chapter.content || "" }}
              />
            </Box>
          </Box>
        )
    }
  }

  const renderAssessmentContent = (assessmentData: any) => {
    // Prioritize assessmentData from course sections (has user_submission_status)
    // over currentAssessment from Redux (might be stale)
    const assessment = assessmentData || currentAssessment
    
    // If user is taking the assessment, show the AssessmentTaking component
    if (isTakingAssessment && assessment?.id) {
      return (
        <Box sx={{ mt: 2 }}>
          <AssessmentTaking
            assessmentId={assessment.id.toString()}
            embedded={true}
            onComplete={async () => {
              // Exit assessment taking mode
              setIsTakingAssessment(false)
              setShowingAssessmentResults(false)
              
              // Refresh course data to get updated locked/unlocked status
              if (courseId) {
                await dispatch(fetchCourseById(courseId))
              }
              
              // Refresh assessment and enrollment data
              await dispatch(fetchAssessmentById(assessment.id.toString()))
              await dispatch(fetchUserEnrollments())
            }}
            onCancel={() => {
              // Cancel taking assessment
              setIsTakingAssessment(false)
              setShowingAssessmentResults(false)
            }}
          />
        </Box>
      )
    }
    
    // Check if assessment has been completed by checking enrollment status
    const assessmentEnrollment = userEnrollments.find(
      (e: any) => e.assignment?.assessment?.id === assessment?.id
    )
    
    // Check if assessment has submissions - enrollment can be 'completed' or 'graded' (for assessments)
    // Also check if user has passed based on submission status
    const hasSubmissions = (
      assessmentEnrollment?.status === 'completed' || 
      (assessmentEnrollment as any)?.status === 'graded' ||
      assessment?.user_submission_status?.has_submission ||
      (assessment?.user_submission_status?.best_score !== undefined && assessment?.user_submission_status?.best_score !== null)
    ) || false
    
    const bestScore = assessment?.user_submission_status?.best_score ?? 0
    const minPassPercentage = assessment?.min_pass_percentage ?? 60
    const hasPassed = (assessmentEnrollment?.status === 'completed') || (bestScore >= minPassPercentage)

    const statusColor = hasPassed ? 'success' : 'error'
    const StatusIcon = hasPassed ? CheckCircle : Cancel
    const statusTitle = hasPassed ? 'Assessment Completed!' : 'Assessment Failed'
    const statusMessage = hasPassed ? 'You have successfully passed this assessment' : 'You did not pass this assessment'

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Assessment: {assessment?.title || 'Loading...'}
        </Typography>
        <Typography variant="body1" paragraph>
          {assessment?.description || 'Test your understanding.'}
        </Typography>
        
        {!currentAssessment && currentItemType === 'assessment' ? (
          // Loading assessment details
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading assessment details...
            </Typography>
          </Box>
        ) : hasSubmissions ? (
          // Show completion status with score details
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, bgcolor: `${statusColor}.light`, border: '1px solid', borderColor: `${statusColor}.main` }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <StatusIcon color={statusColor} sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h5" sx={{ color: `${statusColor}.dark` }}>
                    {statusTitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {statusMessage}
                  </Typography>
                </Box>
              </Box>

              {/* Display score information if available */}
              {assessment?.user_submission_status?.best_submission && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ color: bestScore >= minPassPercentage ? 'success.dark' : 'error.dark', fontWeight: 'bold' }}>
                        {assessment.user_submission_status.best_score?.toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your Score
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {assessment.user_submission_status.best_submission.points_earned || 0}/
                        {assessment.user_submission_status.best_submission.total_points || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Points Earned
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {assessment.min_pass_percentage || 60}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Required to Pass
                      </Typography>
                    </Box>
                  </Box>
                  {assessment.user_submission_status.total_attempts && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                      Completed in {assessment.user_submission_status.total_attempts} {assessment.user_submission_status.total_attempts === 1 ? 'attempt' : 'attempts'}
                    </Typography>
                  )}
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                {enrollment?.assignment?.show_results && assessment.user_submission_status?.has_submission && (
                  <Button
                    variant="outlined"
                    color="info"
                    size="large"
                    startIcon={<VisibilityIcon />}
                    onClick={() => {
                      setSelectedResultsAssessment({ 
                        id: assessment.id, 
                        title: assessment.title 
                      })
                      setShowResultsDialogOpen(true)
                    }}
                  >
                    Show Results
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => {
                    setIsTakingAssessment(true)
                    setShowingAssessmentResults(false)
                  }}
                >
                  Retake Assessment
                </Button>
              </Box>
            </Box>
          </Paper>
        ) : (
          // Show "Start Assessment" button
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                Assessment Ready
              </Typography>
              <Typography variant="body2" paragraph sx={{ color: 'text.secondary' }}>
                You can now take this assessment. Click the button below to begin.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => {
                  // Start taking assessment inline for course-embedded assessments
                  if (assessment?.course) {
                    setIsTakingAssessment(true)
                    setShowingAssessmentResults(false)
                  } else {
                    // For standalone assessments, navigate to dedicated page
                    navigate(`/assessments/${assessment?.id}`)
                  }
                }}
              >
                Start Assessment
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/courses')}>
          Back to Course Catalog
        </Button>
      </Box>
    )
  }

  if (!course) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Course not found.
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/courses')}>
          Back to Course Catalog
        </Button>
      </Box>
    )
  }

  // Check if user is enrolled
  // Wait for enrollments to load before showing the "not enrolled" screen
  if (!enrollmentsLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Checking enrollment status...
        </Typography>
      </Box>
    )
  }
  
  if (!enrollment) {
    return (
      <Box>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/courses')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1">
            {course.title}
          </Typography>
        </Box>

        <Card sx={{ maxWidth: 600, mx: 'auto' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              You are not enrolled in this course
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              To access this course content, you need to enroll first.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/courses')}
              >
                Back to Catalog
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleEnroll}
                disabled={isLoading}
              >
                {isLoading ? 'Enrolling...' : 'Enroll Now'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    )
  }

  const currentChapter = chapters[currentChapterIndex]
  const currentItem = itemSequence.find(item => item.id === currentItemId)
  const currentSequenceIndex = itemSequence.findIndex(item => item.id === currentItemId)
  
  const isLastItem = currentSequenceIndex === itemSequence.length - 1
  const isFirstItem = currentSequenceIndex === 0
  const isChapterCompleted = completedChapters.has(currentChapterIndex)
  const courseProgress = enrollment ? parseFloat(String(enrollment.progress || '0')) : 0
  
  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/courses')}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {course.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Chip
              label={course.difficulty}
              color="primary"
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              Progress: {Math.round(courseProgress)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={courseProgress}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      </Box>

  <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Chapter List Sidebar */}
        <Paper
          ref={sidebarRef}
          sx={{
            width: { xs: 300, sm: 300 },
            p: 2,
            // Stretch the left menu to (near) full viewport height and keep it visible
            height: { xs: 'calc(100vh - 120px)', sm: 'calc(100vh - 120px)' },
            overflowY: 'auto',
            overflowX: 'hidden',
            // Make sidebar stick to top so it remains visible while scrolling main content
            position: 'sticky',
            top: 16,
            alignSelf: 'flex-start',
            // Responsive font adjustments and tighter spacing on small screens
            '& .MuiTypography-root': {
              fontSize: { xs: '0.85rem', sm: '1rem' }
            },
            '& .MuiListItem-root': {
              paddingTop: { xs: '6px', sm: '8px' },
              paddingBottom: { xs: '6px', sm: '8px' }
            }
          }}
        >
          <Typography variant="h6" gutterBottom>
            Course Content
          </Typography>
          
          {/* Display structured sections or flat chapters */}
          {course.sections && course.sections.length > 0 ? (
            // Hierarchical structure with sections
            course.sections.map((section) => (
              <Box key={section.id} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                  {section.title}
                </Typography>
                {section.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    {section.description}
                  </Typography>
                )}
                <List sx={{ pl: 2, pr: 1, '& .MuiListItemText-root': { fontSize: { xs: '0.85rem', sm: '0.95rem' } } }}>
                  {section.items?.map((item) => {
                    if (item.item_type === 'chapter' && item.chapter_detail) {
                      const chapterIndex = chapters.findIndex(ch => ch.id === item.chapter_detail?.id);
                      if (chapterIndex === -1 || !item.chapter_detail) return null;
                      
                      const isSelected = currentItemId === `chapter-${item.chapter_detail.id}`
                      const isLocked = item.computed_is_locked !== undefined ? item.computed_is_locked : (item.is_unlocked !== undefined ? !item.is_unlocked : item.is_locked ?? false)
                      const prerequisiteNames = getPrerequisiteNames(item)
                      
                      return (
                        <ListItem key={item.id} disablePadding>
                          <ListItemButton
                            selected={isSelected}
                            onClick={() => {
                              // Prevent navigation if locked
                              if (!isLocked) {
                                handleItemClick(`chapter-${item.chapter_detail!.id}`, 'chapter')
                              }
                            }}
                            sx={{
                              opacity: isLocked ? 0.6 : 1,
                              cursor: isLocked ? 'not-allowed' : 'pointer',
                              '&:hover': {
                                backgroundColor: isLocked ? 'transparent' : undefined
                              }
                            }}
                          >
                            <ListItemIcon>
                              {isLocked ? (
                                <Lock sx={{ color: 'text.disabled' }} />
                              ) : completedChapters.has(chapterIndex) ? (
                                <CheckCircle color="success" />
                              ) : isSelected ? (
                                <PlayArrow color="primary" />
                              ) : (
                                <RadioButtonUnchecked />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <span style={{ color: isLocked ? '#9e9e9e' : 'inherit' }}>
                                    {item.chapter_detail.title}
                                  </span>
                                  {isLocked && prerequisiteNames && (
                                    <Tooltip 
                                      title={`Complete these first: ${prerequisiteNames}`}
                                      arrow
                                      placement="right"
                                    >
                                      <Info 
                                        sx={{ 
                                          fontSize: '16px', 
                                          color: 'info.main', 
                                          cursor: 'help',
                                          '&:hover': {
                                            color: 'info.dark'
                                          }
                                        }} 
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              }
                              secondary="Chapter"
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    } else if (item.item_type === 'assessment' && item.assessment_detail) {
                      const isSelected = currentItemId === `assessment-${item.assessment_detail.id}`
                      const isLocked = item.computed_is_locked !== undefined ? item.computed_is_locked : (item.is_unlocked !== undefined ? !item.is_unlocked : item.is_locked ?? false)
                      const isCompleted = isAssessmentCompleted(item.assessment_detail.id)
                      const isFailed = isAssessmentFailed(item.assessment_detail.id)
                      const prerequisiteNames = getPrerequisiteNames(item)
                      
                      return (
                        <ListItem key={item.id} disablePadding>
                          <ListItemButton
                            selected={isSelected}
                            onClick={() => {
                              // Prevent navigation if locked
                              if (!isLocked) {
                                handleItemClick(`assessment-${item.assessment_detail!.id}`, 'assessment')
                              }
                            }}
                            sx={{ 
                              backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.12)' : 'rgba(25, 118, 210, 0.04)',
                              opacity: isLocked ? 0.6 : 1,
                              cursor: isLocked ? 'not-allowed' : 'pointer',
                              '&:hover': {
                                backgroundColor: isLocked ? 'rgba(25, 118, 210, 0.04)' : 'rgba(25, 118, 210, 0.08)',
                              },
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                              }
                            }}
                          >
                            <ListItemIcon>
                              {isLocked ? (
                                <Lock sx={{ color: 'text.disabled' }} />
                              ) : isCompleted ? (
                                <CheckCircle color="success" />
                              ) : isFailed ? (
                                <Cancel color="error" />
                              ) : (
                                <Quiz color="primary" />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <span style={{ 
                                    fontWeight: 500,
                                    color: isLocked ? '#9e9e9e' : undefined 
                                  }}>
                                    {item.assessment_detail.title}
                                  </span>
                                  {isLocked && prerequisiteNames && (
                                    <Tooltip 
                                      title={`Complete these first: ${prerequisiteNames}`}
                                      arrow
                                      placement="right"
                                    >
                                      <Info 
                                        sx={{ 
                                          fontSize: '16px', 
                                          color: 'info.main', 
                                          cursor: 'help',
                                          '&:hover': {
                                            color: 'info.dark'
                                          }
                                        }} 
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              }
                              secondary="Assessment"
                              sx={{
                                '& .MuiListItemText-primary': {
                                  fontWeight: 500,
                                  color: isLocked ? 'text.disabled' : 'primary.main'
                                }
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    }
                    return null;
                  })}
                </List>
              </Box>
            ))
          ) : (
            // Flat structure - original chapter list
            <List sx={{ pl: 1, pr: 1, '& .MuiListItemText-root': { fontSize: { xs: '0.85rem', sm: '0.95rem' } } }}>
              {chapters.map((chapter, index) => (
                <ListItem key={chapter.id} disablePadding>
                  <ListItemButton
                    selected={index === currentChapterIndex}
                    onClick={() => handleChapterClick(index)}
                  >
                    <ListItemIcon>
                      {completedChapters.has(index) ? (
                        <CheckCircle color="success" />
                      ) : index === currentChapterIndex ? (
                        <PlayArrow color="primary" />
                      ) : (
                        <RadioButtonUnchecked />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={chapter.title}
                      secondary="Chapter"
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1 }}>
          {currentItem ? (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  {currentItemType === 'chapter' && currentChapter ? 
                    getContentIcon(currentChapter.content_type) : 
                    <Quiz />
                  }
                  <Typography variant="h5" component="h2">
                    {currentItem.title}
                  </Typography>
                  {currentItem.is_locked && (
                    <Chip 
                      label="Locked" 
                      color="warning" 
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>

                {currentItem.is_locked ? (
                  <Box sx={{ 
                    p: 3, 
                    bgcolor: 'warning.light', 
                    color: 'warning.contrastText', 
                    borderRadius: 2,
                    textAlign: 'center' 
                  }}>
                    <Typography variant="h6" gutterBottom>
                      Content Locked
                    </Typography>
                    <Typography variant="body1">
                      Complete the prerequisite content to unlock this {currentItemType}.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {currentItemType === 'chapter' && currentChapter ? 
                      renderChapterContent(currentChapter) : 
                      renderAssessmentContent(currentItem.data)
                    }
                  </>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Navigation */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={<NavigateBefore />}
                    onClick={handlePrevItem}
                    disabled={isFirstItem}
                  >
                    Previous
                  </Button>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Show completion button for chapters that aren't locked or completed */}
                    {(() => {
                      if (currentItemType !== 'chapter') return null
                      
                      // Check completion from both sources for debugging
                      const chapterProgressCompleted = completedChapters.has(currentChapterIndex)
                      const itemProgressCompleted = currentItem?.data?.user_progress?.completed || false
                      
                      // Debug logging
                      if (process.env.NODE_ENV === 'development') {
                        console.log('Chapter completion check:', {
                          itemId: currentItemId,
                          chapterIndex: currentChapterIndex,
                          chapterProgressCompleted,
                          itemProgressCompleted,
                          courseItemId: currentItem?.courseItemId,
                          isLocked: currentItem?.is_locked
                        })
                      }
                      
                      // Use item progress if available (more accurate), otherwise fall back to chapter progress
                      const isCompleted = itemProgressCompleted || chapterProgressCompleted
                      
                      if (!currentItem.is_locked && !isCompleted) {
                        return (
                          <Button
                            variant="contained"
                            color="success"
                            onClick={handleChapterComplete}
                          >
                            Mark as Complete
                          </Button>
                        )
                      }
                      return null
                    })()}

                    {/* 
                      Assessment completion is handled automatically when the assessment is passed.
                      No manual "Mark as Complete" button is needed for assessments - the backend
                      signal (handle_assessment_submission) automatically marks the CourseItemProgress
                      as completed when the assessment is graded and passed.
                    */}

                    {/* Show different states based on completion and position */}
                    {enrollment && enrollment.progress >= 100 ? (
                      <Chip
                        label="Course Completed!"
                        color="success"
                        icon={<CheckCircle />}
                      />
                    ) : !isLastItem ? (
                      <Button
                        variant="contained"
                        endIcon={<NavigateNext />}
                        onClick={handleNextItem}
                        disabled={currentSequenceIndex < itemSequence.length - 1 && itemSequence[currentSequenceIndex + 1].is_locked}
                      >
                        {itemSequence[currentSequenceIndex + 1]?.type === 'assessment' ? 'Next Assessment' : 'Next Chapter'}
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">
                  No content available for this course.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      {/* Error Dialog */}
      <Dialog 
        open={showErrorDialog} 
        onClose={() => setShowErrorDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>
          Unable to Complete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: 'pre-line', mt: 1 }}>
            {errorDialogMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowErrorDialog(false)} 
            variant="contained"
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Show Results Dialog */}
      {selectedResultsAssessment && (
        <ShowResultsDialog
          open={showResultsDialogOpen}
          onClose={() => {
            setShowResultsDialogOpen(false)
            setSelectedResultsAssessment(null)
          }}
          assessmentId={selectedResultsAssessment.id}
          assessmentTitle={selectedResultsAssessment.title}
        />
      )}
    </Box>
  )
}

export default CourseViewPage
// Fixed HMR issue
